import { useColorScheme } from '@mui/material/styles';
import type { AgChartOptions } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import type { AgCartesianAxesOptions } from 'ag-charts-types';
import { useCallback, useMemo } from 'react';

import { hasDataGreaterThanZero } from '@/utils/Common.utils';

import { getAgChartsTheme, noDataRenderer } from '../charts.utils';
import type { StackedAreaChartProps } from './StackedAreaChart.types';

const StackedAreaChart = (props: StackedAreaChartProps) => {
  const { data, series, handleChartClick } = props;
  const { mode: themeMode } = useColorScheme();

  const hasVisibleData = useMemo(
    () => hasDataGreaterThanZero(data || []),
    [data]
  );

  const getFormattedAxes = useCallback((): AgCartesianAxesOptions => {
    if (!data?.length || !series?.length) {
      return {};
    }

    return {
      x: {
        type: 'unit-time',
        position: 'bottom',
        label: {
          autoRotate: true,
          format: '%b %d'
        },
        tick: {
          enabled: true
        },
        line: {
          enabled: hasVisibleData
        }
      },
      y: {
        type: 'number',
        position: 'left',
        line: {
          enabled: hasVisibleData
        }
      }
    };
  }, [data, series, hasVisibleData]);

  const options: AgChartOptions = useMemo(
    () => ({
      data: hasVisibleData ? data : [],
      series,
      background: {
        fill: 'transparent'
      },
      theme: getAgChartsTheme(themeMode),
      axes: getFormattedAxes(),
      legend: {
        enabled: hasVisibleData,
        spacing: 16
      },
      listeners: {
        seriesNodeClick: ({ datum, yKey }) => {
          handleChartClick?.(yKey as string, datum.date);
        }
      },
      padding: {
        bottom: 0,
        right: 28,
        top: 28
      },
      animation: {
        enabled: true
      },
      overlays: {
        noData: {
          renderer: () => noDataRenderer()
        }
      }
    }),
    [data, series, hasVisibleData, themeMode, handleChartClick]
  );

  return <AgCharts style={{ height: '100%' }} options={options} />;
};

StackedAreaChart.displayName = 'StackedAreaChart';

export default StackedAreaChart;
